import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from './support/store';
import {
  checkProductsLoaded,
  checkCiscoProductsLoaded
} from "./support/slices/productsSlice";
import { useMoodleUserByEmail } from './hooks/useMoodleHooks';
import { setMoodleUser, clearMoodleUserForInstance } from './support/slices/moodleSlice';
import PortalShellHeader from './components/Common/PortalShellHeader';
import OpportunityDetail from './components/Opportunity/OpportunityDetail';
import OpportunityListPage from './components/Opportunity/OpportunityListPage';
import Dashboard from './components/Dashboard/Dashboard';
import Login from './components/Common/Login';
import EnablementRoutes from './components/Enablement/EnablementRoutes';
import MspServiceCreation from './components/Mssp/MspServiceCreation';
import PartnerDetail from './components/Mssp/PartnerDetail';
import { useAuthenticateUser } from './hooks/useAuthenticateUser';
import OAuthCallback from './components/Common/OAuthCallback';
import { LoginState } from './types/Session';
import {
  HbrSpinner,
} from '@harbor/elements-react-standalone';
import CustomerListPage from './components/Customer/CustomerListPage';
import { ModalProvider } from './components/security-toolbox-ui/StbModalContext';
import ApprovalsListPage from './components/Approvals/ApprovalsListPage';
import { hideReloginModal, sessionRecoveryFailed } from './support/slices/uiSlice';
import { reset as resetUser } from './support/slices/userSlice';
import SessionRecoveryOverlay from './components/Common/SessionRecoveryOverlay';
import { useInitialMoodleData } from './hooks/useInitialMoodleData';
import ProductDetail from './components/Solutions/ProductDetail';
import LeaderDashboardPage from './components/Leader/LeaderDashboardPage';
import ArchitectureDetail from './components/Solutions/ArchitectureDetail/ArchitectureDetail';
import ArchitectureList from './components/Solutions/ArchitectureList';
import ProductList from './components/Solutions/ProductList';
import Solutions from './components/Solutions/Solutions';
import ArchitecturePage from './components/Solutions/ArchitecturePage/ArchitecturePage';
import { ARCHITECTURE_SLUGS } from './hooks/gtm-architecture-hooks';
import P2P from './components/P2P/P2P';
import CyberBytesList from './components/CyberBytes/CyberBytesList';
import CyberBytesSeriesPage from './components/CyberBytes/CyberBytesSeriesPage';
import Unauthorized from './components/Common/Unauthorized';
import ErrorPage from './components/Common/ErrorPage';
import AdminUserAccess from './components/Admin/PageStructure/AdminUserAccess';
import AdminDataManagement from './components/Admin/PageStructure/AdminDataManagement';
import AdminToolboxConfig from './components/Admin/PageStructure/AdminToolboxConfig';
import Admin from './components/Admin/PageStructure/Admin';
import ProductEditorPage from './components/Admin/ProductEditorPage';
import GoToMarketEditorPage from './components/Admin/GoToMarketEditorPage';
import {
  ADMIN_URL_DATA_MANAGMENT,
  ADMIN_URL_ENABLEMENT,
  ADMIN_URL_LABS_EVENTS,
  ADMIN_URL_LABS_GENERATION,
  ADMIN_URL_TOOLBOX_CONFIG,
  ADMIN_URL_USER_ACCESS
} from './components/Admin/PageStructure/admin-values';
import WorkshopsRoutes from './components/Workshops/WorkshopsRoutes';
import LearningCourseRoute from './components/Common/Learning/LearningCourseRoute';
import { EventSummaryPage } from './components/Common/Moodle/Event/EventSummaryPage';
import AdminEnablement from './components/Admin/PageStructure/AdminEnablement';
import AdminLabGeneration from './components/Admin/PageStructure/AdminLabGeneration';
import AdminLabEvents from './components/Admin/PageStructure/AdminLabEvents';
import AdminSafe from './components/Admin/Safe/AdminSafe';
import AdminAssessments from './components/Admin/Assessments/AdminAssessments';
import AdminCampaigns from './components/Admin/Campaigns/AdminCampaigns';
import SafeSalesforceRequired from './components/Safe/SafeSalesforceRequired';
import { hasSalesforceSession } from './hooks/useSalesforceSession';
import { initializeRegistry } from './support/slices/registrySlice';
import { isExpiredJwt } from './support/queriesHelpers';
import LandingPageV2 from './components/LandingPage/LandingPageV2';
import ReleaseNotesModal from './components/Common/ReleaseNotesModal';
import DocsDrawer from './components/Documentation/DocsDrawer';
import { usePopulateHistory } from './hooks/useNavigationHistory';
import CiscoSSOCallback from './components/Common/CiscoSSOCallback';
import IncubationTeamOpportunityList from './components/Opportunity/IncubationTeam/IncubationTeamOpportunityList';
import { useReleaseVersions } from './hooks/jira-release-notes-hooks';
import { useUserRegistry } from './hooks/useUserRegistry';
import LabRoutes from './components/Lab/LabRoutes';


const CustomerDetailPage = lazy(() => import('./components/Customer/CustomerDetailPage'));

// Lazy: the flow diagram and the xlsx export make the SAFE assessments a large chunk, and most
// users never open one.
const SafeRoutes = lazy(() => import('./components/Safe/SafeRoutes'));
const EngagementRoutes = lazy(() => import('./components/Engagements/EngagementRoutes'));

// Lazy: the assessment builder pulls in a stack of dialogs most users never open, and the fill-in
// surface is only reached from the Resources menu.
const AssessmentRoutes = lazy(() => import('./components/Assessments/AssessmentRoutes'));

// Lazy: the campaign resource surface is only reached from the Resources menu.
const CampaignRoutes = lazy(() => import('./components/Campaigns/CampaignRoutes'));

// Lazy: the report-layout builder pulls in react-grid-layout for its drag/resize
// canvas — kept out of the assessment-admin chunk since most admins never open it.
const AssessmentReportBuilderPage = lazy(
  () => import('./components/Admin/Assessments/AssessmentReportBuilderPage')
);

type ProtectedRouteProps = {
  allow: boolean;
  children: React.ReactElement;
  unauthorizedTo?: string;
};

function ProtectedRoute({ allow, children, unauthorizedTo = '/unauthorized' }: ProtectedRouteProps) {
  if (!allow) return <Navigate to={unauthorizedTo} replace />;
  return children;
}

const ROUTES = {
  home: { path: '/' },

  dashboard: { path: '/dashboard', pageView: 'page.dashboard.view', beta: true },
  leader: { path: '/leader', pageView: 'page.leader.view' },
  customers: { path: '/customers', pageView: 'page.customers.view' },
  customerDetail: { path: '/customer/:id', pageView: 'page.customers.view' },
  opportunities: { path: '/opportunity', pageView: 'page.opportunities.view' },
  opportunityDetail: { path: '/opportunity/:id', pageView: 'page.opportunities.view' },
  opportunityIncubationTeamList: {
    path: '/opportunity-team/:supportPathId?',
    pageView: 'page.opportunities.view',
  },

  enablement: { path: '/enablement/*', pageView: 'page.enablement.view' },
  workshops: { path: '/workshops/*', pageView: 'page.workshops.view' },

  // Lab scenarios, runs and event operations. Splat: LabRoutes owns everything below /lab.
  lab: { path: '/lab/*', pageView: 'page.labs.view', beta: true },

  eventSummary: {
    path: '/workshops/event-summary/:courseId/:eventId',
    pageView: 'page.workshops.view',
  },

  learningCourse: {
    path: '/learning/:provider/:instance/course/:courseId',
    pageView: 'page.workshops.view',
    beta: true,
  },

  mssp: { path: '/mssp', pageView: 'page.mssp.view' },
  msspDetail: { path: '/mssp/:id', pageView: 'page.mssp.view' },
  approvals: { path: '/approvals', pageView: 'page.approvals.view' },
  admin: { path: '/admin', pageView: 'page.admin.view' },
  p2p: { path: '/p2p', pageView: 'page.p2p.view' },
  // Cyber Bytes reuses the page.p2p.view permission.
  cyberBytes: { path: '/cyber-bytes', pageView: 'page.p2p.view' },
  cyberBytesSeries: { path: '/cyber-bytes/:id', pageView: 'page.p2p.view' },

  // Customer workshops, and the SAFE assessment recorded against one. Splat: EngagementRoutes owns
  // the workshop, and SafeWorkflow the steps beneath it.
  //
  // `/workshops` is taken by the training surface (`page.workshops.view`), which is a different thing
  // entirely, hence `engagements`. Gated on `page.safe.view` for now because SAFE is the only
  // workshop type there is; a second type will want its own view permission.
  engagements: { path: '/engagements/*', pageView: 'page.safe.view' },
  // The old standalone SAFE paths. Kept as redirects into the list — an assessment now belongs to a
  // workshop, so `/safe/risk` no longer names a document.
  safe: { path: '/safe/*', pageView: 'page.safe.view' },
  // The mapping administration. A sibling of the `/admin` tree rather than a child of it, because
  // the `SAFE Admin` role holds `page.safeAdmin.view` without `page.admin.view` and the parent
  // route's guard would otherwise reject it before this route was ever reached.
  safeAdmin: { path: '/admin/safe/:section?', pageView: 'page.safeAdmin.view' },

  // The reusable-assessment surfaces. `assessments` is the fill-in catalogue under Resources; it is
  // not gated on Salesforce, unlike SAFE, because an assessment need not belong to a customer.
  // `assessmentAdmin` is the builder, a sibling of the `/admin` tree for the same reason as
  // `safeAdmin`: the `Assessment Admin` role holds `page.assessmentAdmin.view` without
  // `page.admin.view`, so nesting it under the parent guard would lock it out.
  assessment: { path: '/assessments/*', pageView: 'page.assessment.view' },
  assessmentAdmin: { path: '/admin/assessments/:section?', pageView: 'page.assessmentAdmin.view' },
  // The dedicated report-layout builder for one assessment. Same guard as the
  // builder; declared as its own route so its two-segment path outranks the
  // single-segment `:section?` above (React Router v6 ranks by specificity).
  assessmentReport: { path: '/admin/assessments/:id/report', pageView: 'page.assessmentAdmin.view' },

  // The modular-campaign surfaces. `campaign` is the resource catalogue under Resources; like
  // assessments it is not gated on Salesforce. `campaignAdmin` is the builder/administration, a
  // sibling of the `/admin` tree for the same reason as `safeAdmin`/`assessmentAdmin`: the
  // `Campaign Admin` role holds `page.campaignAdmin.view` without `page.admin.view`, so nesting it
  // under the parent guard would lock it out.
  campaign: { path: '/campaigns/*', pageView: 'page.campaign.view' },
  campaignAdmin: { path: '/admin/campaigns/:section?', pageView: 'page.campaignAdmin.view' },

  portal: { path: '/portal' },
  unauthorized: { path: '/unauthorized' },
  error: { path: '/error' },
} as const;

const BETA_PERMISSION = 'page.beta';

const App = () => {
  const {
    sessionRecovery,
    minimizeMenu: menuMinimized
  } = useSelector((state: RootState) => state.ui);
  const [authInProgress, setAuthInProgress] = useState(false);

  const { BRANCH_NAME } = process.env;

  const dispatch = useAppDispatch();
  const {
    signinState,
    login,
    startCiscoSSOLogin,
    name,
    email,
    logout
  } = useAuthenticateUser();

  const toolboxToken = useSelector((state: RootState) => state.user.toolboxToken);
  const sfToken = useSelector((state: RootState) => state.user.sfToken);
  const authz = useSelector((state: RootState) => state.user?.authz);
  // SAFE needs a Salesforce session on top of `page.safe.view`; see `useSalesforceSession`.
  const safeSessionReady = hasSalesforceSession(sfToken, authz);
  const authProvider = useSelector((state: RootState) => state.user.authProvider);

  const location = useLocation();
  const isLearningFullscreen =
    location.pathname.startsWith('/learning/') &&
    new URLSearchParams(location.search).get('fullscreen') === '1';
  const isPortalFullscreen = location.pathname === '/portal';
  const isOAuthCallback =
    location.pathname.startsWith('/oauth/') ||
    location.pathname.startsWith('/ui/oauth/') ||
    location.pathname.startsWith('/oidc/callback');
  const isPortalPath =
    location.pathname === '/portal' ||
    location.pathname === '/portalv2' ||
    location.pathname === '/identity-segmentation' ||
    location.pathname === '/power-security-ops';
  const isPortal = isPortalPath || isOAuthCallback;
  // The portal landing (/portal) now carries the full shared app header. The other standalone
  // portal/marketing pages keep their own chrome (no shell header), and the OAuth callback has none.
  const showShellHeader = !isOAuthCallback && (!isPortalPath || location.pathname === '/portal');
  const currentPath = location.pathname + location.search;

  // ---------- Auth bootstrap guard to prevent route tree flicker ----------
  const [authResolved, setAuthResolved] = useState(false);
  useEffect(() => {
    if (
      signinState === LoginState.LoggedIn ||
      signinState === LoginState.LoggedOut ||
      signinState === LoginState.Error
    ) {
      setAuthResolved(true);
    } else {
      setAuthResolved(false);
    }
  }, [signinState]);

  const uiPermSet = useMemo(
    () => new Set<string>(authz?.uiPermissions || []),
    [authz?.uiPermissions]
  );
  // `admin.full` is a BACKEND permission, not a UI permission: the backend's _build_ui_permissions
  // expands a full admin into the concrete page.*.view list and never emits the literal
  // "admin.full" into uiPermissions. So it is exposed as authz.isAdmin (backend:
  // is_admin = "admin.full" in permissions) and in authz.permissions. Reading it off uiPermissions
  // made isAdminFull permanently false — harmless for canAccessPage (admins hold every page perm
  // anyway) but it made the Hub gate bounce EVERY admin to the legacy Toolbox. Read the real signal.
  const isAdminFull =
    !!authz?.isAdmin ||
    (authz?.permissions || []).includes('admin.full') ||
    uiPermSet.has('admin.full');

  const canAccessPage = (pageViewPerm?: string, opts?: { beta?: boolean }) => {
    if (isAdminFull) return true;
    if (!pageViewPerm) return false;
    if (!uiPermSet.has(pageViewPerm)) return false;
    if (opts?.beta && !uiPermSet.has(BETA_PERMISSION)) return false;
    return true;
  };

  const canSeeDashboard = canAccessPage(ROUTES.dashboard.pageView, { beta: ROUTES.dashboard.beta });

  const canSeeEnablement = canAccessPage(ROUTES.enablement.pageView);
  const canSeeWorkshops = canAccessPage(ROUTES.workshops.pageView);
  const canSeeP2p = canAccessPage(ROUTES.p2p.pageView);
  const canSeeCyberBytes = canAccessPage(ROUTES.cyberBytes.pageView);

  const canUseMoodle = canSeeEnablement || canSeeWorkshops;

  // `page.labs.view` is not seeded yet, so the enablement/workshops pair carries the page until
  // it is. Without the OR the lab area would be visible to admin.full and nobody else.
  const canSeeLabs =
    canAccessPage(ROUTES.lab.pageView, { beta: ROUTES.lab.beta }) || canSeeEnablement || canSeeWorkshops;

  // Stocking pools, assigning pods, enrolling agents, pushing a step at running labs. The
  // backend authorizes each of these again against the caller's token - it wants the pool's
  // contact or staff, and staff only for an unscoped push - so this decides what is offered,
  // not what is permitted.
  const canStewardLabs = canAccessPage(ROUTES.admin.pageView) || canSeeWorkshops;

  // A toolbox JWT that is still present in redux but EXPIRED must not read as authenticated: every
  // db/ai/queue request runs its token through firstUsableToken (which rejects a spent JWT) and then
  // silently falls back to the Salesforce token, so an expired toolbox token 403s those endpoints
  // (the SF token is not valid for them) while the app still believes the session is live. Treating a
  // spent toolbox JWT as unauthenticated instead trips the existing relogin/recovery flow.
  const toolboxTokenUsable = !!toolboxToken && !isExpiredJwt(toolboxToken);

  const isFullyAuthenticated =
    signinState === LoginState.LoggedIn &&
    toolboxTokenUsable &&
    !!authz &&
    (authProvider === 'cisco_sso' || !!sfToken);

  // Added: auth hydration gate.
  // This delays protected route rendering until auth state is truly settled.
  const authHydrated =
    signinState === LoginState.LoggedOut ||
    signinState === LoginState.Error ||
    (
      signinState === LoginState.LoggedIn &&
      toolboxTokenUsable &&
      !!authz &&
      (authProvider === 'cisco_sso' || !!sfToken)
    );

  const canBootstrapMoodleData =
    authResolved &&
    authHydrated &&
    isFullyAuthenticated &&
    !authInProgress &&
    !isOAuthCallback &&
    canUseMoodle;

  // Moodle user lookup should only happen once auth is fully settled.
  // This reduces noisy pre-login / callback fetches.
  const canLookupMoodleUser =
    isFullyAuthenticated &&
    !!email &&
    authResolved &&
    authHydrated &&
    !authInProgress &&
    !isOAuthCallback &&
    canUseMoodle;

  const { data: externalMoodleUser } = useMoodleUserByEmail(
    email,
    { enabled: canLookupMoodleUser },
    'external'
  );

  const { data: internalMoodleUser } = useMoodleUserByEmail(
    email,
    { enabled: canLookupMoodleUser },
    'internal'
  );

  useEffect(() => {
    if (canLookupMoodleUser) {
      dispatch(
        setMoodleUser({
          instance: 'external',
          id: externalMoodleUser?.id ?? null,
          data: externalMoodleUser ?? null,
        })
      );

      dispatch(
        setMoodleUser({
          instance: 'internal',
          id: internalMoodleUser?.id ?? null,
          data: internalMoodleUser ?? null,
        })
      );
      return;
    }

    // Clear Moodle user state if auth is terminal OR the user simply does not
    // have permission to access Moodle-backed areas.
    if (
      signinState === LoginState.LoggedOut ||
      signinState === LoginState.Error ||
      (isFullyAuthenticated && !canUseMoodle)
    ) {
      dispatch(clearMoodleUserForInstance('external'));
      dispatch(clearMoodleUserForInstance('internal'));
    }
  }, [
    canLookupMoodleUser,
    signinState,
    isFullyAuthenticated,
    canUseMoodle,
    externalMoodleUser,
    internalMoodleUser,
    dispatch,
  ]);

  useEffect(() => {
    if (isFullyAuthenticated) {
      dispatch(checkProductsLoaded());
      dispatch(checkCiscoProductsLoaded());
    }
  }, [dispatch, isFullyAuthenticated]);

  useEffect(() => {
    if (signinState === LoginState.LoggedOut || signinState === LoginState.Error) {
      dispatch(hideReloginModal());
      setAuthInProgress(false);
    }
  }, [signinState, dispatch]);

  useEffect(() => {
    if (isFullyAuthenticated) {
      setAuthInProgress(false);
    }
  }, [isFullyAuthenticated]);

  const userId = useSelector((state: RootState) => state.user.userData.id);
  const registry = useSelector((state: RootState) => state.registry.registry);
  const { updateUserNotifications } = useUserRegistry();

  useEffect(() => {
    if (isFullyAuthenticated && userId) {
      dispatch(initializeRegistry());
    }
  }, [isFullyAuthenticated, userId, dispatch]);

  const handleRelogin = () => {
    setAuthInProgress(true);
    // Return the user to exactly where the session expired, not the app root. refreshOn401
    // also captures this, but the periodic validateSession path reaches recovery without it.
    localStorage.setItem('postLoginPath', currentPath);
    // Deliberately do NOT dismiss the recovery overlay here: it must keep covering the app
    // through the redirect so no sensitive data flashes on screen. login() is a full-page
    // redirect (and startCiscoSSOLogin ends in one), so the overlay is torn down by the reload.
    // Re-authenticate with the provider they signed in with. A Cisco SSO user has no
    // Salesforce session to refresh, so login() (the Salesforce flow) would strand them.
    if (authProvider === 'cisco_sso') {
      void startCiscoSSOLogin().catch(() => {
        setAuthInProgress(false);
        dispatch(sessionRecoveryFailed());
      });
    } else {
      login();
    }
  };

  // The accordion fallback: a Salesforce user whose session expired can re-authenticate with
  // Cisco SSO instead. Keeps the overlay up through the redirect, same as handleRelogin.
  const handleReloginWithCiscoSSO = () => {
    setAuthInProgress(true);
    localStorage.setItem('postLoginPath', currentPath);
    void startCiscoSSOLogin().catch(() => {
      setAuthInProgress(false);
      dispatch(sessionRecoveryFailed());
    });
  };

  usePopulateHistory();

  useInitialMoodleData({
    enabled: canBootstrapMoodleData,
  });

  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  const [documentationOpen, setDocumentationOpen] = useState(false);

  const { data: versionsData, isLoading: versionsLoading, error: versionsError } = useReleaseVersions(
    { include_released: true, include_unreleased: true, maxResults: 50 },
    { enabled: isFullyAuthenticated }
  );

  const unReadNotificationsCount = useMemo(() => {
    if (!versionsData || versionsLoading || versionsError || !registry) return 0;

    const active = (versionsData?.values ?? []).filter((v) => v.released);
    const readDate = registry?.notifications?.releaseNotesReadDate
      ? new Date(registry.notifications.releaseNotesReadDate)
      : null;
    if (!readDate) return active.length;

    const unread = active.filter((v) => new Date(v.releaseDate) > readDate);
    return unread.length;
  }, [versionsData, registry, versionsLoading, versionsError]);

  const handleOpenReleaseNotes = (event?: any) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setReleaseNotesOpen(true);
  };

  const handleCloseReleaseNotes = async () => {
    setReleaseNotesOpen(false);
    updateUserNotifications({
      ...registry?.notifications,
      releaseNotesReadDate: new Date().toISOString(),
    });
  };

  const handleOpenDocumentation = (event?: any) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setDocumentationOpen(true);
  };

  const HomeElement = () => {
    // Added: do not redirect to unauthorized while auth is still settling.
    if (!authHydrated || (signinState === LoginState.LoggedIn && !isFullyAuthenticated)) {
      return (
        <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
          <HbrSpinner size="large">
            <div slot="label">Finishing sign-in...</div>
          </HbrSpinner>
        </div>
      );
    }
    // Every user — Salesforce and Cisco SSO alike — defaults to the portal landing. Managers
    // can still open the Leader page from the header; it is no longer the default landing.
    return <Navigate to={ROUTES.portal.path} replace />;
  };

  const showAuthLoadingScreen =
    !isOAuthCallback &&
    (
      !authResolved ||
      !authHydrated ||
      (signinState === LoginState.LoggedIn && !isFullyAuthenticated)
    );

  // Watchdog: the auth loading screen must never spin forever. A hung OAuth exchange (the backend
  // never answers, or answers with an error the callback couldn't recover from), a signinState
  // stuck at SigningIn, or a permissions/authz fetch that keeps failing all leave
  // showAuthLoadingScreen true with no path out — the user just watches "Finishing sign-in..." /
  // "Loading permissions..." indefinitely. Cap it: if the loading screen is still up after this
  // window, reset to a usable state so the login screen (or, mid-recovery, the blocking re-login
  // overlay) replaces the spinner and the user can retry. A real, still-in-flight redirect unloads
  // the page and clears this timer, so this only fires when the app genuinely stays mounted and
  // stuck. The window is generous so a merely-slow (not hung) sign-in is never cut short.
  const AUTH_LOADING_WATCHDOG_MS = 30_000;
  useEffect(() => {
    if (!showAuthLoadingScreen) return;
    const timer = setTimeout(() => {
      console.warn(
        '[AUTH] Sign-in did not complete within the watchdog window; resetting to the login screen.'
      );
      // Clear the stuck session (moves signinState off SigningIn/partial-LoggedIn to LoggedOut).
      dispatch(resetUser());
      // If a recovery flow was underway, turn its spinner into the actionable re-login prompt
      // rather than dropping the overlay silently.
      if (sessionRecovery !== 'idle') {
        dispatch(sessionRecoveryFailed());
      }
    }, AUTH_LOADING_WATCHDOG_MS);
    return () => clearTimeout(timer);
  }, [showAuthLoadingScreen, sessionRecovery, dispatch]);

  return (
    <ModalProvider>
      <div className="app-container">
        {showShellHeader && (
          <PortalShellHeader
            unReadNotificationsCount={unReadNotificationsCount}
            onOpenReleaseNotes={handleOpenReleaseNotes}
            onOpenDocumentation={handleOpenDocumentation}
            isAuthenticated={isFullyAuthenticated}
          />
        )}

        <div
          className="app-body"
          style={
            !isFullyAuthenticated
              ? {
                backgroundImage: `url('/toolbox_background.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                minHeight: 'calc(100vh - 65px)',
              }
              : {}
          }
        >
          
          <div
            className={`app-content ${
              isLearningFullscreen ? 'learning-fullscreen-content' : ''
            }
            ${isPortal ? 'portal-content' : ''}`}
          >
            <Suspense fallback={<HbrSpinner />}>
              {showAuthLoadingScreen ? (
                <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                  <HbrSpinner size="large">
                    <div slot="label">
                      {signinState === LoginState.LoggedIn ? 'Loading permissions...' : 'Loading session...'}
                    </div>
                  </HbrSpinner>
                </div>
              ) : (
                <Routes>
                  {/* Always-available routes */}
                  <Route path="/oauth/_callback" element={<OAuthCallback />} />
                  <Route path="/ui/oauth/_callback" element={<OAuthCallback />} />
                  <Route path="/oidc/callback" element={<CiscoSSOCallback />} />
                  <Route path="/oidc/callback/" element={<CiscoSSOCallback />} />
                  {/* Go-to-market architecture pages — one generic page, content per slug. */}
                  {ARCHITECTURE_SLUGS.map((slug) => (
                    <Route
                      key={slug}
                      path={`/${slug}`}
                      element={<ArchitecturePage slug={slug} />}
                    />
                  ))}

                  {/* System pages */}
                  <Route path={ROUTES.unauthorized.path} element={<Unauthorized />} />
                  <Route
                    path={ROUTES.error.path}
                    element={<ErrorPage title="Error" message="An error occurred while loading the page." />}
                  />

                  {isFullyAuthenticated ? (
                    <>
                      <Route path={ROUTES.home.path} element={<HomeElement />} />
                      <Route path={ROUTES.portal.path} element={<LandingPageV2 />} />

                      <Route
                        path={ROUTES.dashboard.path}
                        element={
                          <ProtectedRoute allow={canSeeDashboard}>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.leader.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.leader.pageView)}>
                            <LeaderDashboardPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.customers.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.customers.pageView)}>
                            <CustomerListPage branch={BRANCH_NAME} />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.customerDetail.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.customerDetail.pageView)}>
                            <CustomerDetailPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.opportunities.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.opportunities.pageView)}>
                            <OpportunityListPage branch={BRANCH_NAME} />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.opportunityIncubationTeamList.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.opportunities.pageView)}>
                            <IncubationTeamOpportunityList />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.opportunityDetail.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.opportunityDetail.pageView)}>
                            <OpportunityDetail />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.enablement.path}
                        element={
                          <ProtectedRoute
                            allow={canAccessPage(ROUTES.enablement.pageView)}
                          >
                            <EnablementRoutes />
                          </ProtectedRoute>
                        }
                      />

                      {/*
                        Course level details are linked from both enablement (journey maps)
                        and workshops/leader tables, so either permission grants access.
                      */}
                      <Route
                        path={ROUTES.learningCourse.path}
                        element={
                          <ProtectedRoute allow={canSeeEnablement || canSeeWorkshops}>
                            <LearningCourseRoute />
                          </ProtectedRoute>
                        }
                      />

                      {/*
                        Event level details are also linked from the leader dashboard's
                        workshop tables, so leaders reach them without full workshops access.
                        Ranks above /workshops/* because its segments are more specific.
                      */}
                      <Route
                        path={ROUTES.eventSummary.path}
                        element={
                          <ProtectedRoute
                            allow={canSeeWorkshops || canAccessPage(ROUTES.leader.pageView)}
                          >
                            <EventSummaryPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.workshops.path}
                        element={
                          <ProtectedRoute
                            allow={canAccessPage(ROUTES.workshops.pageView)}
                          >
                            <WorkshopsRoutes />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.lab.path}
                        element={
                          <ProtectedRoute allow={canSeeLabs}>
                            <LabRoutes canSteward={canStewardLabs} />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.mssp.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.mssp.pageView)}>
                            <MspServiceCreation />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.msspDetail.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.msspDetail.pageView)}>
                            <PartnerDetail />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.approvals.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.approvals.pageView)}>
                            <ApprovalsListPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.p2p.path}
                        element={
                          <ProtectedRoute allow={canSeeP2p}>
                            <P2P />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.cyberBytes.path}
                        element={
                          <ProtectedRoute allow={canSeeCyberBytes}>
                            <CyberBytesList />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.cyberBytesSeries.path}
                        element={
                          <ProtectedRoute allow={canSeeCyberBytes}>
                            <CyberBytesSeriesPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path={ROUTES.engagements.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.engagements.pageView)}>
                            {/* `api/db/safe/*` and `api/db/workshop/*` both refuse a caller without
                                a Salesforce session, and the list resolves account names through
                                Salesforce as well, so the page is swapped for the connect prompt
                                rather than loaded and left to 403. Not an `unauthorizedTo`
                                redirect: the permission is held, only the session is missing. */}
                            {safeSessionReady ? <EngagementRoutes /> : <SafeSalesforceRequired />}
                          </ProtectedRoute>
                        }
                      />

                      {/* Redirects only — see SafeRoutes. Guarded the same way so a bookmark from
                          before the move lands on the list rather than on /unauthorized. */}
                      <Route
                        path={ROUTES.safe.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.safe.pageView)}>
                            <SafeRoutes />
                          </ProtectedRoute>
                        }
                      />

                      {/* Resources → Assessments: the published catalogue and the fill-in form.
                          No Salesforce gate — an assessment need not hang off a customer account. */}
                      <Route
                        path={ROUTES.assessment.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.assessment.pageView)}>
                            <AssessmentRoutes />
                          </ProtectedRoute>
                        }
                      />

                      {/* Resources → Campaigns: the published catalogue and the read-only detail.
                          No Salesforce gate — a campaign need not hang off a customer account. */}
                      <Route
                        path={ROUTES.campaign.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.campaign.pageView)}>
                            <CampaignRoutes />
                          </ProtectedRoute>
                        }
                      />

                      {/* The dedicated report-layout builder for one assessment. Declared before
                          the `:section?` route so its more specific two-segment path wins; same
                          guard as the builder. */}
                      <Route
                        path={ROUTES.assessmentReport.path}
                        element={
                          <ProtectedRoute
                            allow={
                              canAccessPage(ROUTES.assessmentReport.pageView) ||
                              canAccessPage(ROUTES.admin.pageView)
                            }
                          >
                            <Admin />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<AssessmentReportBuilderPage />} />
                      </Route>

                      {/* Declared before the `/admin` tree, same as `/admin/safe`, so the guard
                          that applies is `page.assessmentAdmin.view`, not the parent's. */}
                      <Route
                        path={ROUTES.assessmentAdmin.path}
                        element={
                          <ProtectedRoute
                            allow={
                              canAccessPage(ROUTES.assessmentAdmin.pageView) ||
                              canAccessPage(ROUTES.admin.pageView)
                            }
                          >
                            <Admin />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<AdminAssessments />} />
                      </Route>

                      {/* Declared before the `/admin` tree, same as `/admin/safe`, so the guard
                          that applies is `page.campaignAdmin.view`, not the parent's. */}
                      <Route
                        path={ROUTES.campaignAdmin.path}
                        element={
                          <ProtectedRoute
                            allow={
                              canAccessPage(ROUTES.campaignAdmin.pageView) ||
                              canAccessPage(ROUTES.admin.pageView)
                            }
                          >
                            <Admin />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<AdminCampaigns />} />
                      </Route>

                      {/* Declared before the `/admin` tree so the guard that applies to
                          /admin/safe is this one, not the parent's `page.admin.view`. */}
                      <Route
                        path={ROUTES.safeAdmin.path}
                        element={
                          <ProtectedRoute
                            allow={
                              canAccessPage(ROUTES.safeAdmin.pageView) ||
                              canAccessPage(ROUTES.admin.pageView)
                            }
                          >
                            <Admin />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<AdminSafe />} />
                      </Route>

                      <Route
                        path={ROUTES.admin.path}
                        element={
                          <ProtectedRoute allow={canAccessPage(ROUTES.admin.pageView)}>
                            <Admin />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<Navigate to={`${ADMIN_URL_USER_ACCESS}`} replace />} />
                        <Route path={`${ADMIN_URL_DATA_MANAGMENT}/products`} element={<AdminDataManagement />}>
                          <Route path={`${ADMIN_URL_DATA_MANAGMENT}/products/:id`} element={<ProductEditorPage />} />
                        </Route>
                        <Route path={`${ADMIN_URL_DATA_MANAGMENT}/goToMarkets`} element={<AdminDataManagement />}>
                          <Route path="/admin/dataManagement/goToMarkets/:id" element={<GoToMarketEditorPage />} />
                        </Route>
                        <Route path={`${ADMIN_URL_TOOLBOX_CONFIG}/:section?/:tab1?/:tab2?/:tab3?/:tab4?`} element={<AdminToolboxConfig />} />
                        <Route path={`${ADMIN_URL_USER_ACCESS}/:section?`} element={<AdminUserAccess />} />
                        <Route path={`${ADMIN_URL_DATA_MANAGMENT}/:section?/:tab1?`} element={<AdminDataManagement />} />
                        <Route path={`${ADMIN_URL_ENABLEMENT}/:section?`} element={<AdminEnablement />} />
                        {/* Authoring a lab and running an event for one are two pages, the way
                            they are two halves everywhere else in the lab code. */}
                        <Route path={`${ADMIN_URL_LABS_GENERATION}/:section?`} element={<AdminLabGeneration />} />
                        <Route path={`${ADMIN_URL_LABS_EVENTS}/:section?`} element={<AdminLabEvents />} />
                      </Route>

                      {canAccessPage(ROUTES.admin.pageView) && (
                        <Route path="/solutions" element={<Solutions />}>
                          <Route index element={<Navigate to="/solutions/architecture" replace />} />
                          <Route path="/solutions/product" element={<ProductList />} />
                          <Route path="/solutions/architecture" element={<ArchitectureList />} />
                          <Route path="/solutions/product/:id" element={<ProductDetail />} />
                          <Route path="/solutions/architecture/:id" element={<ArchitectureDetail />} />
                        </Route>
                      )}

                      <Route path="*" element={<ErrorPage title="Not Found" message="The page does not exist." />} />
                    </>
                  ) : (
                    <>
                      <Route
                        path="/"
                        element={<Login onLogin={login} onAuthStart={() => setAuthInProgress(true)} />}
                      />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </>
                  )}
                </Routes>
              )}
            </Suspense>
          </div>

          <ReleaseNotesModal
            open={releaseNotesOpen}
            onClose={handleCloseReleaseNotes}
          />

          <DocsDrawer
            open={documentationOpen}
            onClose={() => setDocumentationOpen(false)}
          />
        </div>

        <ReactQueryDevtools initialIsOpen={false} />

        {/* Session recovery covers the app the moment a 401 is seen: an opaque login background is
            drawn over the still-mounted app (route and context preserved) so no sensitive data is
            exposed. While 'attempting' it shows a spinner during the silent re-auth; if that fails
            it becomes the blocking login prompt. Not shown on the OAuth callback route, which is
            mid-handshake and has no session to protect. */}
        {sessionRecovery !== 'idle' && !isOAuthCallback && (
          <SessionRecoveryOverlay
            loading={sessionRecovery === 'attempting' || authInProgress}
            authProvider={authProvider}
            onLogin={handleRelogin}
            onCiscoSSO={handleReloginWithCiscoSSO}
          />
        )}
      </div>
    </ModalProvider>
  );
};

export default App;